import { ref } from 'vue';
import type { Ref } from 'vue';

import { userApi, type UserStatus } from '@/entities/User';
import { downloadBlobFile, useCustomToast } from '@/shared/lib';
import type { MemberSearchField, SearchFilter, SortOrder } from '@/shared/lib';

function toOptionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function useAdminUsersExport(options: {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filter: Ref<SearchFilter>;
  searchFieldId: Ref<MemberSearchField>;
}) {
  const toast = useCustomToast();
  const isExportInProgress = ref(false);

  const buildExportParams = () => {
    const f = options.filter.value;
    const rawStatus = f.status;
    const status: UserStatus | undefined =
      rawStatus === 'ACTIVE' || rawStatus === 'INACTIVE' ? rawStatus : undefined;

    return {
      search: options.searchTerm.value.trim() || undefined,
      sort: options.sort.value || undefined,
      order: (options.order.value as SortOrder | undefined) || undefined,
      searchField: options.searchFieldId.value,
      positionId: toOptionalNumber(f.positionId),
      functionalActivityId: toOptionalNumber(f.functionalActivityId),
      status,
      admissionDate: f.admissionDate as string | undefined,
      dismissalDate: f.dismissalDate as string | undefined,
    };
  };

  const handleExportToExcel = async () => {
    if (isExportInProgress.value) {
      return;
    }

    isExportInProgress.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const { blob, exportedCount, filename } = await userApi.exportUsersExcel(buildExportParams());

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1 ? '1 usuário exportado' : `${exportedCount} usuários exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch {
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: 'Tente novamente.',
        type: 'error',
      });
    } finally {
      isExportInProgress.value = false;
    }
  };

  return {
    handleExportToExcel,
  };
}
